import { motion } from "framer-motion";
import { Check } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const ITEMS = [
  "Technical SEO",
  "Performance",
  "Analytics",
  "Conversion paths",
  "Structured data",
  "Accessibility",
  "Mobile optimization",
  "Scalability",
];

/**
 * "More than a website" — sits between the work showcase and the tech section.
 * Same engineered visual language as the rest of the site.
 * Reinforces that SEO / performance / accessibility are part of every build,
 * not an extra deck item.
 */
export default function MoreThanWebsite() {
  return (
    <section
      data-nav-dark
      data-testid="more-than-website-section"
      className="relative overflow-hidden bg-[#050507] py-28 text-white md:py-36 lg:py-44"
    >
      <div className="grid-faint pointer-events-none absolute inset-0 opacity-[0.04]" />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.85, ease: EASE }}
          className="mb-16 grid items-end gap-10 md:mb-24 md:grid-cols-12"
        >
          <div className="md:col-span-8">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-px w-8 bg-white/30" />
              <span className="ff-ui text-[11px] uppercase tracking-[0.35em] text-white/55">
                More than a website
              </span>
            </div>
            <h2 className="ff-display text-4xl font-bold leading-[1] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-[5.4rem]">
              A website should do{" "}
              <span className="ff-serif font-normal text-white/55">
                more than look good.
              </span>
            </h2>
          </div>
          <div className="md:col-span-4">
            <p className="ff-body max-w-md text-base leading-relaxed text-white/55">
              It should help people find you, understand what you offer, and
              become customers.
            </p>
          </div>
        </motion.div>

        <motion.ul
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-10%" }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.05 } } }}
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
        >
          {ITEMS.map((item) => (
            <motion.li
              key={item}
              variants={{
                hidden: { opacity: 0, y: 14 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
              }}
              className="group flex items-center gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.015] px-5 py-5 backdrop-blur-[2px] transition-all duration-500 hover:border-white/[0.16] hover:bg-white/[0.035]"
            >
              <span className="grid h-7 w-7 place-items-center rounded-full border border-white/[0.10] text-white/55 transition-colors duration-500 group-hover:border-[#E4FF00]/40 group-hover:text-[#E4FF00]">
                <Check size={13} strokeWidth={2} />
              </span>
              <span className="ff-body text-[14.5px] text-white/80 transition-colors duration-500 group-hover:text-white">
                {item}
              </span>
            </motion.li>
          ))}
        </motion.ul>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
          className="ff-serif mt-14 max-w-2xl text-lg text-white/50"
        >
          We don&apos;t believe in launching beautiful websites that nobody can find.
        </motion.p>
      </div>
    </section>
  );
}
